import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import AxiosInstance from "../../../helpers/api";
import { useTranslation } from "react-i18next";
import "./SupportChatWidget.scss";

const SupportChatWidget = () => {
  const { t } = useTranslation();
  const authUser = useSelector((state) => state?.authentication?.user);
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState("list"); // "list" | "new" | "conversation"
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [messageText, setMessageText] = useState("");
  const [error, setError] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);

  // Only show for logged-in users
  if (!authUser?.jwt_token) return null;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await AxiosInstance.get("/api/v1/support/tickets/");
      if (res.data.status) {
        setTickets(res.data.tickets);
        // Count tickets with unread admin replies
        const unread = res.data.tickets.filter(
          (t) => t.last_message?.is_admin && t.status !== "closed"
        ).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      console.error("Failed to fetch support tickets", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (ticketId) => {
    try {
      setLoading(true);
      const res = await AxiosInstance.get(
        `/api/v1/support/tickets/${ticketId}/messages/`
      );
      if (res.data.status) {
        setActiveTicket(res.data.ticket);
        setMessages(res.data.messages);
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      setError(t('support.loadMessagesFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!subject.trim() || !messageText.trim()) {
      setError(t('support.subjectAndMessageRequired'));
      return;
    }
    try {
      setLoading(true);
      setError("");
      const res = await AxiosInstance.post("/api/v1/support/tickets/", {
        subject: subject.trim(),
        message: messageText.trim(),
      });
      if (res.data.status) {
        setSubject("");
        setMessageText("");
        setView("conversation");
        await fetchMessages(res.data.ticket.id);
        fetchTickets();
      } else {
        setError(res.data.message || t('support.createTicketFailed'));
      }
    } catch (err) {
      setError(t('support.createTicketFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !activeTicket) return;
    try {
      setLoading(true);
      setError("");
      const res = await AxiosInstance.post(
        `/api/v1/support/tickets/${activeTicket.id}/messages/`,
        { content: messageText.trim() }
      );
      if (res.data.status) {
        setMessageText("");
        await fetchMessages(activeTicket.id);
      } else {
        setError(res.data.message || t('support.sendMessageFailed'));
      }
    } catch (err) {
      setError(t('support.sendMessageFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (view === "new") handleCreateTicket();
      else if (view === "conversation") handleSendMessage();
    }
  };

  const openTicket = (ticket) => {
    setView("conversation");
    fetchMessages(ticket.id);
  };

  const toggleOpen = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next) {
      setView("list");
      setError("");
      fetchTickets();
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: "#3498db",
      in_progress: "#f39c12",
      resolved: "#2ecc71",
      closed: "#95a5a6",
    };
    return colors[status] || "#999";
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="scw">
      {/* Floating button */}
      <button className="scw__fab" onClick={toggleOpen} title={t('support.supportChat')}>
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
        {!isOpen && unreadCount > 0 && (
          <span className="scw__badge">{unreadCount}</span>
        )}
      </button>

      {/* Chat panel */}
      {isOpen && (
        <div className="scw__panel">
          {/* Header */}
          <div className="scw__header">
            {view !== "list" && (
              <button
                className="scw__back"
                onClick={() => {
                  setView("list");
                  setError("");
                  fetchTickets();
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18l-6-6 6-6"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            <span className="scw__title">
              {view === "list"
                ? t('support.support')
                : view === "new"
                ? t('support.newTicket')
                : activeTicket?.subject || t('support.conversation')}
            </span>
          </div>

          {/* Body */}
          <div className="scw__body">
            {error && <div className="scw__error">{error}</div>}

            {/* TICKET LIST VIEW */}
            {view === "list" && (
              <>
                <button
                  className="scw__new-btn"
                  onClick={() => {
                    setView("new");
                    setError("");
                  }}
                >
                  + {t('support.newSupportTicket')}
                </button>
                {loading ? (
                  <div className="scw__loading">{t('support.loading')}</div>
                ) : tickets.length === 0 ? (
                  <div className="scw__empty">
                    {t('support.noTickets')}
                  </div>
                ) : (
                  <div className="scw__ticket-list">
                    {tickets.map((tk) => (
                      <div
                        key={tk.id}
                        className="scw__ticket-item"
                        onClick={() => openTicket(tk)}
                      >
                        <div className="scw__ticket-top">
                          <span className="scw__ticket-subject">
                            {tk.subject}
                          </span>
                          <span
                            className="scw__ticket-status"
                            style={{
                              background: getStatusColor(tk.status),
                            }}
                          >
                            {tk.status.replace("_", " ")}
                          </span>
                        </div>
                        <div className="scw__ticket-meta">
                          <span>{t('support.messagesCount', { count: tk.message_count })}</span>
                          <span>{formatTime(tk.updated_at)}</span>
                        </div>
                        {tk.last_message && (
                          <div className="scw__ticket-preview">
                            {tk.last_message.is_admin && (
                              <span className="scw__admin-tag">{t('support.admin')}: </span>
                            )}
                            {tk.last_message.content}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* NEW TICKET VIEW */}
            {view === "new" && (
              <div className="scw__new-form">
                <label className="scw__label">{t('support.subject')}</label>
                <input
                  className="scw__input"
                  type="text"
                  placeholder={t('support.subjectPlaceholder')}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={300}
                />
                <label className="scw__label">{t('support.message')}</label>
                <textarea
                  className="scw__textarea"
                  placeholder={t('support.messagePlaceholder')}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={handleKeyPress}
                  rows={4}
                />
                <button
                  className="scw__send-btn"
                  onClick={handleCreateTicket}
                  disabled={loading || !subject.trim() || !messageText.trim()}
                >
                  {loading ? t('support.creating') : t('support.createTicket')}
                </button>
              </div>
            )}

            {/* CONVERSATION VIEW */}
            {view === "conversation" && (
              <>
                {activeTicket && (
                  <div className="scw__ticket-info">
                    <span
                      className="scw__ticket-status"
                      style={{
                        background: getStatusColor(activeTicket.status),
                      }}
                    >
                      {activeTicket.status.replace("_", " ")}
                    </span>
                    <span className="scw__ticket-priority">
                      {activeTicket.priority}
                    </span>
                  </div>
                )}
                <div className="scw__messages">
                  {messages.map((m) => (
                    <div
                      key={m.id}
                      className={`scw__msg ${
                        m.is_admin ? "scw__msg--admin" : "scw__msg--user"
                      }`}
                    >
                      <div className="scw__msg-sender">
                        {m.is_admin ? t('support.supportTeam') : t('support.you')}
                      </div>
                      <div className="scw__msg-content">{m.content}</div>
                      <div className="scw__msg-time">
                        {formatTime(m.created_at)}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                {activeTicket?.status !== "closed" && (
                  <div className="scw__compose">
                    <textarea
                      className="scw__compose-input"
                      placeholder={t('support.typeMessage')}
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={handleKeyPress}
                      rows={2}
                    />
                    <button
                      className="scw__compose-send"
                      onClick={handleSendMessage}
                      disabled={loading || !messageText.trim()}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
                          stroke="#fff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                )}
                {activeTicket?.status === "closed" && (
                  <div className="scw__closed-notice">
                    {t('support.ticketClosed')}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportChatWidget;
